import { useCallback } from 'react'

// A simplified interface for an RTK Query trigger's return value.
// We only care about the `unwrap` method.
interface Unwrappable<T> {
	unwrap: () => Promise<T>
}

// Represents the trigger function from `useLazyQuery` or `useMutation`.
type Trigger<T, A> = (arg: A) => Unwrappable<T>

/**
 * A hook to create a memoized async function from an RTK Query trigger.
 * This abstracts away the need to call `.unwrap()` manually, providing a
 * simple promise-based function.
 *
 * @param trigger The trigger function from a `useLazyQuery` or `useMutation` hook.
 * @returns A memoized async function that returns a promise with the unwrapped result.
 */
export const useUnwrappedTrigger = <T, A>(trigger: Trigger<T, A>) => {
	return useCallback(
		(arg: A): Promise<T> => {
			return trigger(arg).unwrap()
		},
		[trigger],
	)
}
